'use client';

import CloseBlack1414 from '@/svg/main/close_black_14_14.svg';
import { useRouter } from 'next/navigation';
import ImageWithFallback from '../common/image-with-fallback';
import { useEffect, useMemo, useRef, useState } from 'react';
import PinGray1418 from '@/svg/main/travel-card/pin_gray_14_18.svg';
import TimeGray1818 from '@/svg/main/travel-card/time_gray_18_18.svg';
import TagGray1616 from '@/svg/main/travel-card/tag_gray_16_16.svg';
import TicketGray1614 from '@/svg/main/travel-card/ticket_gray_16_14.svg';
import ArrowDownGray148 from '@/svg/main/travel-card/arrow_down_gray_14_8.svg';
import { getTravelCardPoiDetail } from '@/utils/actions';
import { TravelCardPoiCGType, TravelCardPoiOperatingHourCGType } from '@/types/graphql/root';
import PoiDetailSkeleton from './poi-detail-in-travel-card-skeleton';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Image from 'next/image';

export default function PoiDetailInTravelCard({ poiId }: { poiId: string }) {
  const router = useRouter();
  const [poiDetail, setPoiDetail] = useState<TravelCardPoiCGType | null>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [showFullText, setShowFullText] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [isTimeExpanded, setIsTimeExpanded] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (poiId) {
      setIsLoading(true);
      getTravelCardPoiDetail(poiId)
        .then((data) => {
          setPoiDetail(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('### getTravelCardPoiDetail error ###', error);
          setIsLoading(false);
        });
    }
  }, [poiId]);

  useEffect(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      setIsTextTruncated(element.scrollHeight > element.clientHeight || element.scrollHeight > element.offsetHeight);
    }
  }, [poiDetail?.description]);

  const times = useMemo(() => {
    if (!poiDetail?.operatingHours) return [];
    if (isTimeExpanded) return poiDetail.operatingHours;
    return poiDetail.operatingHours.slice(0, 1);
  }, [isTimeExpanded, poiDetail]);

  const timesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (timesRef.current) {
      const $element = timesRef.current;
      if (isTimeExpanded) {
        $element.style.height = `${$element.scrollHeight}px`;
      } else {
        const $firstChild = $element.children[0];
        if ($firstChild) {
          $element.style.height = `${$firstChild.clientHeight}px`;
        } else {
          $element.style.height = `24px`;
        }
      }
    }
  }, [isTimeExpanded]);

  const sliderSettings = {
    dots: false,
    arrows: false,
    autoplay: false,
    infinite: Boolean(poiDetail?.images?.length && poiDetail.images.length > 1),
    afterChange: (current: number) => setActiveSlide(current),
  };

  return (
    <div className='fixed w-full h-dvh max-w-480pxr top-0 z-[2] overflow-hidden travel-card-poi-from-100'>
      <div className='w-full h-dvh'>
        <div className='w-full max-w-480pxr h-56pxr py-16pxr px-20pxr flex justify-between items-center bg-white'>
          <span className='w-24pxr h-24pxr'></span>
          <span className='body-16-md text-new-gray-900'>
            {isLoading ? 'POI 정보 로딩 중' : poiDetail?.name || 'POI 정보'}
          </span>
          <button className='w-24pxr h-24pxr flex items-center justify-center' onClick={() => router.back()}>
            <CloseBlack1414 />
          </button>
        </div>
        <div className='w-full h-[calc(100dvh-56px)] overflow-y-scroll bg-white'>
          {isLoading ? (
            <PoiDetailSkeleton />
          ) : (
            <>
              <div className='relative w-full aspect-[360/200]'>
                {poiDetail?.images && poiDetail.images.length > 0 ? (
                  <Slider {...sliderSettings}>
                    {poiDetail.images.map((imageUrl: string | null, index: number) => (
                      <div key={`slide-${index}`} className='outline-none'>
                        <ImageWithFallback
                          src={imageUrl ?? ''}
                          alt='travel-card-poi-image'
                          width={480}
                          height={360}
                          quality={100}
                          className='w-full aspect-[360/200] object-cover'
                          fallbackSrc={
                            'https://hoolzzuk-public-images.s3.ap-northeast-2.amazonaws.com/cities/square_128x128.png'
                          }
                        />
                      </div>
                    ))}
                  </Slider>
                ) : (
                  <Image
                    src='https://hoolzzuk-public-images.s3.ap-northeast-2.amazonaws.com/cities/square_128x128.png'
                    alt='travel-card-image'
                    width={480}
                    height={360}
                    quality={100}
                    className='w-full aspect-[360/200] object-cover'
                  />
                )}
                {poiDetail?.images && poiDetail.images.length > 1 && (
                  <div className='absolute bottom-20pxr right-20pxr w-full z-10 flex justify-end'>
                    <p className='text-white bg-custom-black/70 px-10pxr py-1 rounded-20pxr body-12-r'>
                      {activeSlide + 1} / {poiDetail.images.length}
                    </p>
                  </div>
                )}
              </div>
              <div className='pt-20pxr px-20pxr flex flex-col gap-8pxr'>
                <div className='flex'>
                  <span className='px-4pxr py-2pxr border border-new-gray-200 bg-new-gray-100 rounded-4pxr body-12-md text-new-gray-700'>
                    {poiDetail?.poiType || 'POI 타입'}
                  </span>
                </div>
                <div className='flex flex-col gap-12pxr'>
                  <div className='flex flex-wrap gap-2pxr items-center'>
                    <p className='heading-18-bold text-new-gray-900'>{poiDetail?.name || 'POI 이름'}</p>
                    <p className='body-12-bold text-new-gray-700'>{poiDetail?.address || 'POI 주소'}</p>
                  </div>
                  <div className='relative'>
                    <div
                      ref={descriptionRef}
                      className={`w-full body-14-regular text-new-gray-800 whitespace-pre-line ${showFullText ? '' : 'line-clamp-3'}`}
                    >
                      {poiDetail?.description || 'POI 설명이 없습니다.'}
                      {showFullText && (
                        <button
                          className='text-new-gray-800 body-14-regular underline pl-5pxr'
                          onClick={() => setShowFullText(false)}
                        >
                          접기
                        </button>
                      )}
                    </div>
                    {isTextTruncated && !showFullText && (
                      <div className='absolute bottom-0 right-0 flex items-center'>
                        <div className='w-30pxr h-20pxr bg-gradient-to-r from-transparent to-white'></div>
                        <button
                          className='text-new-gray-800 body-14-regular underline bg-white pl-2pxr'
                          onClick={() => setShowFullText(true)}
                        >
                          더보기
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className='w-full h-10pxr my-30pxr bg-new-gray-100 border-y border-new-gray-300'></div>
              <div className='px-20pxr pb-80pxr flex flex-col gap-20pxr'>
                <div className='flex flex-col gap-20pxr pb-20pxr'>
                  <p className='sub-title-16-bold text-new-gray-900'>기본 정보</p>
                  <div className='flex flex-col gap-16pxr'>
                    <div className='flex flex-col gap-4pxr'>
                      <div className='flex gap-4pxr items-center'>
                        <span className='w-20pxr h-20pxr flex items-center justify-center'>
                          <PinGray1418 />
                        </span>
                        <span className='body-14-regular text-new-gray-700'>주소</span>
                      </div>
                      <div className='pl-24pxr body-16-regular text-new-gray-900'>
                        {poiDetail?.address || '주소 정보가 없습니다.'}
                      </div>
                    </div>
                    <div className='flex flex-col gap-4pxr'>
                      <div className='flex gap-4pxr items-center'>
                        <span className='w-20pxr h-20pxr flex items-center justify-center'>
                          <TimeGray1818 />
                        </span>
                        <span className='body-14-regular text-new-gray-700'>운영 시간</span>
                      </div>
                      <div className='pl-24pxr flex gap-4pxr'>
                        <div className='flex flex-col gap-4pxr body-16-regular text-new-gray-900'>
                          {times.map((data: TravelCardPoiOperatingHourCGType | null) => (
                            <div key={data?.key} className='flex gap-8pxr'>
                              <span className='flex-none'>{data?.key}</span>
                              <span>{data?.value}</span>
                            </div>
                          ))}
                        </div>
                        <div className={`w-24pxr flex-none flex flex-col justify-end`}>
                          <button
                            className={`w-24pxr h-24pxr flex items-center justify-center ${
                              isTimeExpanded ? 'rotate-180' : ''
                            }`}
                            onClick={() => setIsTimeExpanded(!isTimeExpanded)}
                          >
                            <ArrowDownGray148 />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className='flex flex-col gap-4pxr'>
                      <div className='flex gap-4pxr items-center'>
                        <span className='w-20pxr h-20pxr flex items-center justify-center'>
                          <TagGray1616 />
                        </span>
                        <span className='body-14-regular text-new-gray-700'>추천 방문시기</span>
                      </div>
                      <div className='pl-24pxr body-16-regular text-new-gray-900'>
                        {poiDetail?.bestVisitTime || '정보 없음'}
                      </div>
                    </div>
                    <div className='flex flex-col gap-4pxr'>
                      <div className='flex gap-4pxr items-center'>
                        <span className='w-20pxr h-20pxr flex items-center justify-center'>
                          <TicketGray1614 />
                        </span>
                        <span className='body-14-regular text-new-gray-700'>입장료</span>
                      </div>
                      <div className='pl-24pxr body-16-regular text-new-gray-900'>
                        {poiDetail?.entryFee || '정보 없음'}
                      </div>
                    </div>
                  </div>
                  <div className='border-b boder-new-gray-200'></div>
                  <div className='flex flex-col gap-4pxr'>
                    <div className='flex flex-wrap gap-4pxr'>
                      {poiDetail?.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className='px-4pxr py-2pxr border border-new-point-200 bg-new-point-50 rounded-4pxr body-12-md text-new-point-400'
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
